import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Trash2, KeyRound } from 'lucide-react';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';

type UserRow = { id: string; email: string; full_name: string | null; role: 'owner'|'admin'|'agent'|'viewer'; last_login_at: string | null };

export const SettingsUsersPage: React.FC = () => {
  const { t } = useTranslation();
  const [me, setMe] = useState<UserRow | null>(null);
  const [rows, setRows] = useState<UserRow[]>([]);
  const [invite, setInvite] = useState({ email: '', full_name: '', role: 'agent' as UserRow['role'], password: '', sendEmail: true });
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileActionSheetOpen, setMobileActionSheetOpen] = useState<string | null>(null); // User ID for mobile bottom sheet
  const itemsPerPage = 10;
  const { orgId, currentUser, loading: orgLoading, refresh } = useOrgContext();

  const isAdmin = me && (me.role === 'owner' || me.role === 'admin');

  const loadUsers = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase
      .from('users')
      .select('id,email,full_name,role,last_login_at')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(200);
    if (data) setRows(data as any);
  }, [orgId]);

  useEffect(() => {
    setMe(currentUser as any);
  }, [currentUser]);

  useEffect(() => {
    if (!orgId || orgLoading) return;
    loadUsers();
  }, [orgId, orgLoading, loadUsers]);

  async function onInvite() {
    if (!isAdmin) return alert(t('crm.settings.alerts.notAllowed'));
    if (!orgId) return alert(t('crm.settings.alerts.missingWorkspace'));
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || '';
    const res = await fetch('/api/users/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(invite)
    });
    if (!res.ok) {
      const feedback = await res.json().catch(() => null);
      return alert(feedback?.message || t('crm.settings.alerts.inviteFailed'));
    }
    setInvite({ email: '', full_name: '', role: 'agent', password: '', sendEmail: true });
    setCurrentPage(1); // Reset to first page when new user is added
    await loadUsers();
  }

  async function changeRole(userId: string, role: string) {
    if (!isAdmin) return alert(t('crm.settings.alerts.notAllowed'));
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || '';
    const res = await fetch('/api/users/role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ user_id: userId, role })
    });
    if (!res.ok) return alert(t('crm.settings.alerts.roleChangeFailed'));
    setRows(prev => prev.map(u => u.id === userId ? { ...u, role: role as any } : u));
  }

  async function sendPasswordReset(user: UserRow) {
    if (!isAdmin) return alert(t('crm.settings.alerts.notAllowed'));
    if (!user.email) return alert(t('crm.settings.alerts.userHasNoEmail'));
    const confirmation = me?.id === user.id
      ? t('crm.settings.alerts.resetConfirmSelf')
      : t('crm.settings.alerts.resetConfirmOther', { email: user.email });
    if (!window.confirm(confirmation)) return;

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || '';
    if (!token) return alert(t('crm.settings.alerts.sessionExpired'));

    setResettingId(user.id);
    try {
      const res = await fetch('/api/users/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: user.id, email: user.email }),
      });
      const feedback = await res.json().catch(() => null);
      if (!res.ok) {
        alert(feedback?.message || t('crm.settings.alerts.unableToSendReset'));
        return;
      }
      alert(t('crm.settings.alerts.resetSentSuccess'));
    } finally {
      setResettingId(null);
    }
  }

  async function deleteUser(user: UserRow) {
    if (!isAdmin) return alert(t('crm.settings.alerts.notAllowed'));
    if (!window.confirm(t('crm.settings.alerts.removeAccessConfirm', { email: user.email }))) {
      return;
    }
    if (me?.id === user.id && !window.confirm(t('crm.settings.alerts.deleteSelfConfirm'))) {
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || '';
    if (!token) {
      alert(t('crm.settings.alerts.sessionExpired'));
      return;
    }

    setDeletingId(user.id);
    try {
      const res = await fetch('/api/users/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
      const feedback = await res.json().catch(() => null);
      if (!res.ok) {
        alert(feedback?.message || t('crm.settings.alerts.unableToDeleteUser'));
        return;
      }
      setRows(prev => prev.filter(row => row.id !== user.id));
      if (me?.id === user.id) {
        await supabase.auth.signOut();
        window.location.href = '/login';
      }
      await refresh();
    } finally {
      setDeletingId(null);
    }
  }

  // Pagination logic
  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = rows.slice(startIndex, endIndex);

  if (orgLoading || !me) return <div className="text-gray-900 dark:text-gray-100">{t('crm.settings.users.loading')}</div>;
  if (!isAdmin) return <div className="text-gray-900 dark:text-gray-100">{t('crm.settings.users.adminOnly')}</div>;

  return (
    <div className="space-y-4 lg:space-y-6 w-full max-w-full overflow-x-hidden">
      <h1 className="text-xl sm:text-2xl font-bold md:font-semibold mb-4 text-gray-900 dark:text-white">{t('crm.settings.users.title')}</h1>
      <div className="mb-6 grid gap-3 p-4 sm:p-6 border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-xl md:rounded-lg shadow-md md:grid-cols-2 xl:grid-cols-12">
        <input
          placeholder={t('crm.settings.users.email')}
          className="bg-white dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl md:rounded-lg px-4 py-3.5 md:px-3 md:py-2 text-base md:text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 w-full md:col-span-2 xl:col-span-3 min-h-[48px] md:min-h-0 font-medium"
          value={invite.email}
          onChange={e => setInvite({ ...invite, email: e.target.value })}
        />
        <input
          placeholder={t('crm.settings.invite.fullNamePlaceholder')}
          className="bg-white dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl md:rounded-lg px-4 py-3.5 md:px-3 md:py-2 text-base md:text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 w-full md:col-span-2 xl:col-span-3 min-h-[48px] md:min-h-0 font-medium"
          value={invite.full_name}
          onChange={e => setInvite({ ...invite, full_name: e.target.value })}
        />
        <select className="bg-white dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl md:rounded-lg px-4 py-3.5 md:px-3 md:py-2 text-base md:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 w-full md:col-span-1 xl:col-span-2 min-h-[48px] md:min-h-0 font-medium" value={invite.role} onChange={e => setInvite({ ...invite, role: e.target.value as UserRow['role'] })}>
          <option value="owner">{t('crm.settings.roleOptions.owner')}</option>
          <option value="admin">{t('crm.settings.roleOptions.admin')}</option>
          <option value="agent">{t('crm.settings.roleOptions.agent')}</option>
          <option value="viewer">{t('crm.settings.roleOptions.viewer')}</option>
        </select>
        <div className="space-y-2 md:col-span-2 xl:col-span-3">
          <input
            placeholder={t('crm.settings.invite.temporaryPasswordPlaceholder')}
            className="bg-white dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl md:rounded-lg px-4 py-3.5 md:px-3 md:py-2 text-base md:text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-700/30 min-h-[48px] md:min-h-0 font-medium"
            type="text"
            value={invite.password}
            disabled={invite.sendEmail}
            onChange={e => setInvite({ ...invite, password: e.target.value })}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {invite.sendEmail
              ? t('crm.settings.invite.passwordInstructions')
              : t('crm.settings.invite.passwordManual')}
          </p>
        </div>
        <label className="flex items-center gap-3 text-base md:text-sm text-gray-900 dark:text-white font-medium md:font-normal select-none md:col-span-2 xl:col-span-3 min-h-[48px] md:min-h-0">
          <input
            type="checkbox"
            checked={invite.sendEmail}
            onChange={e => setInvite({ ...invite, sendEmail: e.target.checked, password: e.target.checked ? '' : invite.password })}
            className="w-5 h-5 md:w-4 md:h-4 border-2 border-gray-300 dark:border-gray-600 text-cyan-500 focus:ring-cyan-500 rounded"
          />
                {t('crm.settings.invite.sendEmail')}
        </label>
        <button onClick={onInvite} className="px-5 md:px-4 py-3.5 md:py-2 rounded-xl md:rounded-lg bg-cyan-500 text-white font-bold md:font-semibold hover:bg-cyan-600 active:scale-[0.98] transition-transform shadow-lg hover:shadow-xl md:col-span-2 xl:col-span-1 xl:col-start-12 min-h-[56px] md:min-h-0 text-base md:text-sm">
                {t('crm.settings.invite.inviteButton')}
        </button>
      </div>
      {/* Desktop Table - Hidden on Mobile */}
      <div className="hidden md:block border-2 border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto bg-white dark:bg-gray-800 shadow-md">
        <table className="w-full text-left">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider">{t('crm.settings.users.email')}</th>
              <th className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider">{t('crm.settings.users.name')}</th>
              <th className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider">{t('crm.settings.users.role')}</th>
              <th className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider">{t('crm.settings.users.lastLogin')}</th>
              <th className="px-3 py-2 text-gray-700 dark:text-gray-300 font-semibold text-xs uppercase tracking-wider">{t('crm.settings.users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map(u => (
              <tr key={u.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{u.email}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{u.full_name || '—'}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  <select className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500" value={u.role} onChange={e => changeRole(u.id, e.target.value)}>
                    <option value="owner">{t('crm.settings.roleOptions.owner')}</option>
                    <option value="admin">{t('crm.settings.roleOptions.admin')}</option>
                    <option value="agent">{t('crm.settings.roleOptions.agent')}</option>
                    <option value="viewer">{t('crm.settings.roleOptions.viewer')}</option>
                  </select>
                </td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => sendPasswordReset(u)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-md border border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition-colors disabled:opacity-60"
                      disabled={resettingId === u.id || deletingId === u.id}
                    >
                        {resettingId === u.id ? t('crm.settings.users.resetPasswordSending') : t('crm.settings.users.resetPassword')}
                    </button>
                    <button
                      onClick={() => deleteUser(u)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-md border border-red-500 dark:border-red-400 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-60"
                      disabled={deletingId === u.id || resettingId === u.id}
                    >
                        {deletingId === u.id ? t('crm.settings.users.deleteRemoving') : t('crm.settings.users.delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card List - Only on Mobile */}
      <div className="block md:hidden space-y-3 pb-20">
        {paginatedData.map(u => {
          const roleLabels: Record<string, string> = {
            owner: t('crm.settings.roleOptions.owner'),
            admin: t('crm.settings.roleOptions.admin'),
            agent: t('crm.settings.roleOptions.agent'),
            viewer: t('crm.settings.roleOptions.viewer'),
          };

          return (
            <div
              key={u.id}
              className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm active:scale-[0.98] transition-transform"
            >
              {/* Header: Email/Name (Bold, Large) */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 truncate">
                {u.full_name || u.email}
              </h3>
              
              {/* Subheader: Email (if name exists) or Role (Gray, Small) */}
              {u.full_name && (
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-2 truncate">
                  {u.email}
                </p>
              )}
              
              {/* Body: Role + Last Login */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                    {roleLabels[u.role] || u.role}
                  </span>
                </div>
                {u.last_login_at && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(u.last_login_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              
              {/* Actions: More Options (Three Dots) - Opens Bottom Sheet */}
              <div className="flex justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setMobileActionSheetOpen(u.id);
                  }}
                  className="p-3 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-transform min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                  title={t('crm.settings.moreOptions')}
                >
                  <MoreHorizontal className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: Action Bottom Sheet */}
      {mobileActionSheetOpen && (() => {
        const user = paginatedData.find(u => u.id === mobileActionSheetOpen);
        if (!user) return null;

        const roleLabels: Record<string, string> = {
          owner: t('crm.settings.roleOptions.owner'),
          admin: t('crm.settings.roleOptions.admin'),
          agent: t('crm.settings.roleOptions.agent'),
          viewer: t('crm.settings.roleOptions.viewer'),
        };

        return (
          <div className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={() => setMobileActionSheetOpen(null)}>
            <div 
              className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl shadow-2xl border-t border-gray-200 dark:border-gray-700 transition-transform duration-300 ease-out translate-y-0"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle */}
              <div 
                onClick={() => setMobileActionSheetOpen(null)}
                className="flex items-center justify-center pt-3 pb-2 cursor-pointer touch-none"
              >
                <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
              </div>

              {/* User Info */}
              <div className="px-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  {user.full_name || user.email}
                </h3>
                {user.full_name && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 mb-2">
                    {user.email}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                    {roleLabels[user.role] || user.role}
                  </span>
                  {user.last_login_at && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Ultimo accesso: {new Date(user.last_login_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Role Selector */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('crm.settings.roleLabel')}
                </label>
                <select 
                  className="w-full bg-white dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-base text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 min-h-[48px] font-medium" 
                  value={user.role} 
                  onChange={e => {
                    changeRole(user.id, e.target.value);
                    setMobileActionSheetOpen(null);
                  }}
                >
                  <option value="owner">{t('crm.settings.roleOptions.owner')}</option>
                  <option value="admin">{t('crm.settings.roleOptions.admin')}</option>
                  <option value="agent">{t('crm.settings.roleOptions.agent')}</option>
                  <option value="viewer">{t('crm.settings.roleOptions.viewer')}</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="p-4 space-y-2">
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    setMobileActionSheetOpen(null);
                    await sendPasswordReset(user);
                  }}
                  disabled={resettingId === user.id || deletingId === user.id}
                  className="w-full px-4 py-4 rounded-xl bg-cyan-500 dark:bg-cyan-600 text-white font-semibold text-base hover:bg-cyan-600 dark:hover:bg-cyan-700 active:scale-[0.98] transition-transform shadow-md min-h-[56px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <KeyRound className="w-5 h-5" />
                  {resettingId === user.id ? t('crm.settings.users.resetPasswordSending') : t('crm.settings.users.resetPassword')}
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    setMobileActionSheetOpen(null);
                    await deleteUser(user);
                  }}
                  disabled={deletingId === user.id || resettingId === user.id}
                  className="w-full px-4 py-4 rounded-xl bg-red-600 dark:bg-red-500 text-white font-semibold text-base hover:bg-red-700 dark:hover:bg-red-600 active:scale-[0.98] transition-transform shadow-md min-h-[56px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-5 h-5" />
                  {deletingId === user.id ? t('crm.settings.users.deleteRemoving') : t('crm.settings.users.delete')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pagination Controls - Mobile Optimized */}
      {rows.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 mt-4 px-3 sm:px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl md:rounded-lg shadow-sm">
          <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
            <span className="hidden sm:inline">{t('crm.settings.pagination.showing')} </span>
            {startIndex + 1}-{Math.min(endIndex, rows.length)} <span className="hidden sm:inline">{t('crm.settings.pagination.of')} {rows.length} {t('crm.settings.pagination.users')}</span>
            <span className="sm:hidden"> / {rows.length}</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-center">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-2 sm:px-3 py-2 sm:py-1.5 rounded-xl md:rounded-md border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-medium min-h-[44px] sm:min-h-0"
            >
              <span className="hidden sm:inline">{t('crm.settings.pagination.previous')}</span>
              <span className="sm:hidden">{t('crm.settings.pagination.prevShort')}</span>
            </button>
            <div className="flex items-center gap-1 overflow-x-auto max-w-full sm:max-w-none">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 sm:px-3 py-2 sm:py-1.5 rounded-xl md:rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] sm:min-h-0 ${
                      currentPage === pageNum
                        ? 'bg-cyan-500 text-white'
                        : 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-2 sm:px-3 py-2 sm:py-1.5 rounded-xl md:rounded-md border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm font-medium min-h-[44px] sm:min-h-0"
            >
              <span className="hidden sm:inline">{t('crm.settings.pagination.next')}</span>
              <span className="sm:hidden">{t('crm.settings.pagination.nextShort')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsUsersPage;



